import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

export const SILENCE_MINUTE_MS = 60_000;
// Same measure and threshold as MicLevelBars: time-domain RMS below 0.01 counts as silent.
const SILENT_RMS = 0.01;
// setInterval, not requestAnimationFrame: rAF stops in a background tab, and a vet often records with
// the tab hidden. Elapsed time is measured by the clock, so a throttled interval still counts the minute.
const SAMPLE_MS = 500;

/**
 * R23: true once the stream has stayed below the silence threshold continuously for a minute.
 * Clears as soon as a sample has sound. Inactive (paused, extension, not recording) resets it, so
 * paused time never counts, and a new stream starts fresh.
 */
export const useSilenceMinute = (stream: MediaStream | null, active: boolean): boolean => {
    const [silent, setSilent] = useState(false);

    useEffect(() => {
        setSilent(false);
        const Ctor: typeof AudioContext | undefined =
            typeof window !== "undefined" ? window.AudioContext || (window as any).webkitAudioContext : undefined;
        if (!active || !stream || !Ctor) return;

        let audioContext: AudioContext;
        let analyser: AnalyserNode;
        try {
            audioContext = new Ctor();
            analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            audioContext.createMediaStreamSource(stream).connect(analyser);
            // A suspended context (WebKit outside a user gesture) reads silence, which would raise a false note.
            if (audioContext.state === "suspended") void audioContext.resume().catch(() => undefined);
        } catch {
            return;
        }

        const samples = new Uint8Array(analyser.fftSize);
        // The minute counts from the last sample with sound, or from the start of the active stretch.
        let lastSound = Date.now();
        let noted = false;

        const timer = setInterval(() => {
            analyser.getByteTimeDomainData(samples);
            let sum = 0;
            for (let i = 0; i < samples.length; i++) {
                const v = (samples[i] - 128) / 128;
                sum += v * v;
            }
            const now = Date.now();
            if (Math.sqrt(sum / samples.length) >= SILENT_RMS) {
                lastSound = now;
                if (noted) {
                    noted = false;
                    setSilent(false);
                }
                return;
            }
            if (!noted && now - lastSound >= SILENCE_MINUTE_MS) {
                noted = true;
                setSilent(true);
                Sentry.addBreadcrumb({ category: "recording", level: "info", message: "recording.silence_minute" });
            }
        }, SAMPLE_MS);

        return () => {
            clearInterval(timer);
            audioContext.close().catch(() => undefined);
        };
    }, [stream, active]);

    return silent;
};
